import * as Popover from "@radix-ui/react-popover";
import { HelpCircle } from "lucide-react";
import type { ReactNode } from "react";

import type { MetricKey } from "@busright/data-contract";

import { getMetricDef } from "@/lib/metric-registry";

// =================================================================================================
// MetricTooltip / MetricLabel — "tooltips on every calc" (J + John).
//
// Every displayed metric can show its definition. The registry (`getMetricDef`) supplies the label,
// unit, human formula, and plain-English tooltip. We use a Radix Popover (not a hover tooltip) so it
// is keyboard- and touch-accessible: a real focusable trigger that opens on click/Enter, traps no
// focus, and closes on Escape / outside-click. RAG/score honesty is conveyed in the copy, not color.
//
//   <MetricLabel metricKey="memory.usedMb" />            → "Memory used (?)" with a definition popover
//   <MetricTooltip metricKey="health.composite">…</MetricTooltip>  → wrap any trigger content
//
// If a key is not yet in the registry the components degrade gracefully: `MetricLabel` renders the
// raw fallback text with no info affordance.
// =================================================================================================

// The popover body: label, the unit/score honesty note, plain-English tooltip, and the formula.
const MetricDefinitionCard = ({ metricKey }: { readonly metricKey: MetricKey }): ReactNode => {
  const def = getMetricDef(metricKey);
  if (!def) {
    return null;
  }
  return (
    <div className="metric-def">
      <div className="metric-def-head">
        <span className="metric-def-label">{def.label}</span>
        <span className="metric-def-kind">{def.isHealthScore ? "0–100 score" : "fact"}</span>
      </div>
      <p className="metric-def-tooltip">{def.tooltip}</p>
      <div className="metric-def-formula">
        <span className="metric-def-formula-label">How it's computed</span>
        <code className="br-tabular-nums">{def.formula}</code>
      </div>
    </div>
  );
};

export interface MetricTooltipProps {
  readonly metricKey: MetricKey;
  // The trigger content (the metric's number, label, or icon).
  readonly children: ReactNode;
  // Accessible name for the trigger button when the children aren't self-describing.
  readonly triggerLabel?: string;
}

// Wrap arbitrary content so it opens the metric's definition popover. The trigger is a real button.
export const MetricTooltip = ({
  metricKey,
  children,
  triggerLabel,
}: MetricTooltipProps): ReactNode => {
  const def = getMetricDef(metricKey);
  if (!def) {
    // Unknown metric — render the content inert (no info affordance) rather than a broken popover.
    return <>{children}</>;
  }
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="metric-trigger"
          aria-label={triggerLabel ?? `What is ${def.label}?`}
        >
          {children}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="metric-popover" sideOffset={6} collisionPadding={12}>
          <MetricDefinitionCard metricKey={metricKey} />
          <Popover.Arrow className="metric-popover-arrow" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export interface MetricLabelProps {
  readonly metricKey: MetricKey;
  // Override the registry label (e.g. a surface-specific phrasing). Defaults to the registry label.
  readonly label?: string;
}

// A metric's label rendered with a trailing (?) info affordance that opens its definition. The whole
// label+icon is the trigger so it reads as one control.
export const MetricLabel = ({ metricKey, label }: MetricLabelProps): ReactNode => {
  const def = getMetricDef(metricKey);
  const text = label ?? def?.label ?? metricKey;
  if (!def) {
    return <span className="metric-label">{text}</span>;
  }
  return (
    <MetricTooltip metricKey={metricKey}>
      <span className="metric-label">
        {text}
        <HelpCircle className="metric-label-info" size={12} aria-hidden />
      </span>
    </MetricTooltip>
  );
};
